"use client"

import Text from "@/components/common/Typography/Text"
import { useTETranslation } from "@/hooks/app/useTETranslation"
import { TypeAirport } from "@/lib/redux/features/flight/type"
import { splitMatcher } from "@/utils/string/splitMatcher"
import { faPlane } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Col, Row } from "antd"
import { TitleProps } from "antd/es/typography/Title"
import { FC } from "react"
import { useTranslation } from "react-i18next"

type SearchingAirportItemProps = {
  airport: TypeAirport & { searchValue?: any, matchers?: any },
  onChange?: (airport?: TypeAirport) => void
  layout?: "default" | "grouped"
}

const SearchingAirportItem: FC<SearchingAirportItemProps> = ({ airport, onChange, layout = "default" }) => {
  const { getAirportCityName, getAirportName } = useTETranslation()
  const { i18n } = useTranslation()
  const lenSearchValue = airport?.searchValue?.length
  const matcherNameEn = airport.matchers?.nameEn
  const matcherNameVi = airport.matchers?.nameVi
  const matcherCode = airport.matchers?.code
  const matcherCityVi = airport.matchers?.cityVi
  const matcherCityEn = airport.matchers?.cityEn
  const matcherCountry = airport.matchers?.country

  const name = splitMatcher(getAirportName(airport), i18n.language === 'en' ? matcherNameEn : matcherNameVi, lenSearchValue)
  const code = splitMatcher(airport.code, matcherCode, lenSearchValue)
  const city = airport?.cityEn ? splitMatcher(getAirportCityName(airport), matcherCityEn, lenSearchValue) : splitMatcher(getAirportCityName(airport), matcherCityVi, lenSearchValue)
  const country = splitMatcher(airport.country, matcherCountry, lenSearchValue)

  const renderTitle = (content: any, highlight?: boolean, props?: TitleProps) => {
    if (!content) {
      return null
    }

    const isString = typeof content === 'string'
    const contentSplited = isString ? content.split(/\s+/) : []

    return (
      <Text
        {...props}
        className={`home__searching--placement-title${highlight ? ' home__searching--placement-hightlight' : ''}${props?.className ? ` ${props.className}` : ''}`}
      >
        {isString ? contentSplited.map((c, idx) => {
          if (idx === contentSplited.length - 1 && !!c) {
            return c
          }
          return (
            <>
              {c}&nbsp;
            </>
          )
        }) : content || <></>}
      </Text>
    )
  }

  const renderSubTitle = (content: any, highlight?: boolean, props?: TitleProps) => {
    if (!content) {
      return null
    }

    return (
      <Text {...props} className={`${highlight ? ' home__searching--placement-hightlight' : ''}${props?.className ? ` ${props.className}` : ''}`} type='secondary'>
        {content}
      </Text>
    )
  }

  const titleRow = (
    <Row align='middle'>
      {renderTitle(
        <>
          {
            renderTitle(name[0] || '')
          }{
            renderTitle(name[1] || '', true)
          }{
            renderTitle(name[2] || '')
          } {
            renderTitle((
              <>
                {renderTitle(code[0] || '', false, { type: 'secondary' })}{renderTitle(code[1], true, { type: 'secondary' })}{renderTitle(code[2], false, { type: 'secondary',  })}
              </>
            ), false, { type: 'secondary' })
          }
        </>
      )}
    </Row>
  )

  const isGrouped = layout === "grouped"

  return (
    <Row
      onClick={() => {
        onChange && onChange(airport)
      }}
      style={{ paddingBlock: isGrouped ? "0.65rem" : "1rem" }}
      className={`home__searching--placement-item${isGrouped ? " home__searching--placement-item--grouped" : ""}`}
    >
      <Col flex="none" className="home__searching--placement-item-icon-col">
        <FontAwesomeIcon icon={faPlane} aria-hidden />
      </Col>
      <Col flex="auto">
        {titleRow}
        {!isGrouped && (
          <Row align='middle'>
            {renderSubTitle(
              <>
                {renderSubTitle(city[0] || '')}{renderSubTitle(city[1]|| '', true)}{renderSubTitle(city[2] || '')}{airport?.country ? ', ' : ''}
                {renderSubTitle(country[0] || '')}{renderSubTitle(country[1] || '', true)}{renderSubTitle(country[2] || '')}
              </>, false, { type: 'secondary' }
            )}
          </Row>
        )}
      </Col>
    </Row>
  )
}

SearchingAirportItem.displayName = 'SearchingAirportItem'

export default SearchingAirportItem